import { PltUsersList } from "modules/users/PltUsers/PltUsersList";
import React from "react";
import { useSearchParams } from "react-router-dom";

import { isGUID } from "@bps/common-utils";
import { FilterBar, Item } from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { withPermissions } from "@components/withPermissions";
import { useSearchUsers } from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";
import { ValidationMessages } from "@libs/validation/validation-messages.enum";

interface PltUsersFilterValues {
  searchTerm: string;
  tenantId: string;
  status: string;
}

const usersFilterValidator = new Validator<PltUsersFilterValues>(validator => ({
  tenantId: [
    validator.custom().func<"tenantId">(value => {
      if (!value.trim()) return value;
      return isGUID(value ?? "") ? value : ValidationMessages.guid;
    })
  ]
}));

const PltUsersBase: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get("searchTerm") ?? "";
  const tenantId = searchParams.get("tenantId") ?? "";
  const status = searchParams.get("status") ?? "";

  const { data, isFetching } = useSearchUsers(
    { tenantId, searchTerm },
    { enabled: !!tenantId.trim() || !!searchTerm.trim() }
  );

  const items: Item<PltUsersFilterValues>[] = [
    {
      name: "tenantId",
      type: "searchBox",
      stickItem: true,
      props: {
        placeholder: "Tenant ID",
        removeSpecialCharacters: false,
        styles: { root: { width: 310 } }
      }
    },
    {
      name: "searchTerm",
      type: "searchBox",
      props: {
        placeholder: "User ID / Username",
        styles: { root: { width: 250 } },
        removeSpecialCharacters: false
      }
    },
    {
      name: "status",
      type: "optionsSelect",
      props: {
        placeholder: "Status",
        options: [{ key: "ACTIVE", text: "Active" }],
        hideSearchOption: true,
        styles: { root: { width: 100 } }
      }
    }
  ];

  const users = (data ?? []).filter(u => (status ? u.status === status : true));
  return (
    <>
      <FilterBar<PltUsersFilterValues>
        maxItemWidth={250}
        items={items}
        initialValues={{ tenantId, searchTerm, status }}
        onChange={({ state: { values } }) => {
          setSearchParams(values as any);
        }}
        customResetButtonCondition={(_a, values) => {
          return Object.values(values).every(v => !v);
        }}
        onReset={({ reset }) => {
          reset({ searchTerm: "", tenantId: "", status: "" });
        }}
        validate={usersFilterValidator.validate}
      >
        {() => null}
      </FilterBar>
      <PltUsersList items={users} enableShimmer={isFetching} />
    </>
  );
};

export const PltUsers = withPermissions(
  PltUsersBase,
  [Permissions.PltCatalogUserRead, Permissions.PltCatalogUserWrite],
  "or"
);
